// Settings.js - Main settings component for AI SDR
import React, { useState, useEffect, useContext } from "react";
import {
  FaCog,
  FaUsers,
  FaCreditCard,
  FaEnvelope,
  FaBell,
  FaStar,
  FaRegStar,
  FaSignOutAlt,
} from "react-icons/fa";
import TeamSettings from "./components/TeamSettings";
import { AuthContext } from "../AuthProvider";
import { db } from "../firebase";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import { clearAiSdrAccess } from "./components/AccessCodePopup";
import { useNavigate } from "react-router-dom";

/**
 * Settings Component
 *
 * This component serves as a container for various settings in the AI SDR module.
 * It provides a tabbed interface to access different settings categories.
 */
const Settings = () => {
  const [activeTab, setActiveTab] = useState("team");
  const { user } = useContext(AuthContext);
  const [defaultTeamId, setDefaultTeamId] = useState(null);
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const navigate = useNavigate();

  // Define settings tabs
  const tabs = [
    { id: "team", label: "Team Settings", icon: <FaUsers /> },
    { id: "account", label: "Account", icon: <FaCog /> },
    { id: "billing", label: "Billing & Plans", icon: <FaCreditCard /> },
    { id: "notifications", label: "Notifications", icon: <FaBell /> },
    { id: "email", label: "Email Settings", icon: <FaEnvelope /> },
    { id: "access", label: "Access Control", icon: <FaSignOutAlt /> },
  ];

  useEffect(() => {
    if (user) {
      fetchUserProfile();
    }
  }, [user]);

  const fetchUserProfile = async () => {
    try {
      // Check both user document locations (from login.js pattern)
      const [userDocSnapshot, userNewDocSnapshot] = await Promise.all([
        getDoc(doc(db, "users", user.email)),
        getDoc(doc(db, "usersNew", user.uid)),
      ]);

      // Get default team from either document
      if (
        userNewDocSnapshot.exists() &&
        userNewDocSnapshot.data().defaultTeamId
      ) {
        setDefaultTeamId(userNewDocSnapshot.data().defaultTeamId);
      } else if (
        userDocSnapshot.exists() &&
        userDocSnapshot.data().defaultTeamId
      ) {
        setDefaultTeamId(userDocSnapshot.data().defaultTeamId);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  const setDefaultTeam = async (teamId, teamName) => {
    if (!user) return;

    setIsSettingDefault(true);
    try {
      // Update both user documents (following login.js pattern)
      await Promise.all([
        updateDoc(doc(db, "users", user.email), {
          defaultTeamId: teamId,
          updatedAt: new Date(),
        }),
        updateDoc(doc(db, "usersNew", user.uid), {
          defaultTeamId: teamId,
          updatedAt: new Date(),
        }),
      ]);

      setDefaultTeamId(teamId);
      toast.success(`Set "${teamName}" as your default team`);
    } catch (error) {
      console.error("Error updating default team:", error);
      toast.error("Failed to set default team");
    } finally {
      setIsSettingDefault(false);
    }
  };

  const handleSignOut = () => {
    clearAiSdrAccess();
    toast.success("You have been signed out of AI-SDR");
    navigate("/ai-sdr");
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Settings</h1>
        </div>

        <div className="bg-white shadow rounded-lg">
          {/* Tabs Navigation */}
          <div className="border-b">
            <nav className="flex">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`px-6 py-4 text-sm font-medium flex items-center ${
                    activeTab === tab.id
                      ? "border-b-2 border-purple-600 text-purple-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === "team" && (
              <TeamSettings
                defaultTeamId={defaultTeamId}
                setDefaultTeam={setDefaultTeam}
                isSettingDefault={isSettingDefault}
              />
            )}

            {activeTab === "account" && (
              <div className="text-center py-8 text-gray-500">
                <FaCog className="mx-auto text-4xl mb-4 text-gray-400" />
                <h3 className="text-lg font-medium">Account Settings</h3>
                <p className="mt-2">Account settings will be available soon.</p>
              </div>
            )}

            {activeTab === "billing" && (
              <div className="text-center py-8 text-gray-500">
                <FaCreditCard className="mx-auto text-4xl mb-4 text-gray-400" />
                <h3 className="text-lg font-medium">Billing & Plans</h3>
                <p className="mt-2">
                  Billing and plan options will be available soon.
                </p>
              </div>
            )}

            {activeTab === "notifications" && (
              <div className="text-center py-8 text-gray-500">
                <FaBell className="mx-auto text-4xl mb-4 text-gray-400" />
                <h3 className="text-lg font-medium">Notification Settings</h3>
                <p className="mt-2">
                  Notification preferences will be available soon.
                </p>
              </div>
            )}

            {activeTab === "email" && (
              <div className="text-center py-8 text-gray-500">
                <FaEnvelope className="mx-auto text-4xl mb-4 text-gray-400" />
                <h3 className="text-lg font-medium">Email Settings</h3>
                <p className="mt-2">
                  Email configuration will be available soon.
                </p>
              </div>
            )}

            {activeTab === "access" && (
              <div className="py-8">
                <div className="max-w-md mx-auto">
                  <h3 className="text-lg font-medium mb-4">Access Control</h3>
                  <div className="mb-6">
                    <p className="text-gray-600 mb-4">
                      You're currently signed in to the AI-SDR module. You can
                      sign out to require the access code again.
                    </p>
                    <button
                      onClick={handleSignOut}
                      className="flex items-center px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    >
                      <FaSignOutAlt className="mr-2" /> Sign Out of AI-SDR
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
